import { FC, ReactNode } from 'react'
import { calcularIdade } from '../../../../../../_metronic/helpers'
import { StatusDot } from '../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'
import { AtendimentoView } from '../../../atendimento/core/_models'
import { SITUACAO_INTERCONSULTA } from '../../../pareceres/pareceres-list/core/situacaoInterconsulta'
import { CorrigirLotacaoSolicitante } from '../../atendimentos-medico-list/component/AtendimentoInfo/CorrigirLotacaoSolicitante'
import styles from '../ParecerInterconsulta.module.css'

type Props = {
  atendimento: AtendimentoView
  /** Recarrega o atendimento depois de corrigir a lotação do solicitante. */
  onAtualizar: () => void
}

const Campo: FC<{ rotulo: string; children?: ReactNode }> = ({ rotulo, children }) => (
  <div>
    <dt className='text-gray-700 fs-7 fw-normal'>{rotulo}</dt>
    <dd className='fw-semibold text-gray-900 mb-0'>{children || '-'}</dd>
  </div>
)

/**
 * Quem é o paciente e quem pediu o parecer, na coluna da solicitação. Só o que o teleconsultor
 * precisa para responder: sem presença nem dados ocupacionais (não se aplicam à interconsulta) e
 * sem réplica, que fica na coluna do parecer.
 */
export const SolicitacaoResumo: FC<Props> = ({ atendimento, onAtualizar }) => {
  const { t } = useT()
  const pessoa = atendimento.paciente?.pessoa
  const situacao = atendimento.situacaoInterconsulta
    ? SITUACAO_INTERCONSULTA[atendimento.situacaoInterconsulta]
    : undefined
  const lotacao = atendimento.lotacaoSolicitante

  return (
    <section aria-labelledby='parecer-paciente-titulo' className='card card-bordered'>
      <div className='card-body p-5 d-flex flex-column gap-5'>
        <div>
          <div className='d-flex flex-wrap align-items-baseline gap-2 mb-1'>
            <h2 id='parecer-paciente-titulo' className='fs-3 fw-bolder text-gray-900 mb-0'>
              {atendimento.paciente?.nome}
            </h2>
            {atendimento.paciente?.id && (
              <a
                href={`/apps/atendimentosMedicos/historicoClinico/${atendimento.paciente.id}`}
                target='_blank'
                rel='noopener noreferrer'
                className='fs-7 fw-semibold text-nowrap'
              >
                Ver histórico clínico
                <span className='visually-hidden'> (abre em nova aba)</span>
                <span aria-hidden='true'> ↗</span>
              </a>
            )}
          </div>
          <p className='text-gray-700 fs-7 mb-0'>
            {[
              pessoa?.sexo,
              pessoa?.dataNascimento &&
                `${calcularIdade(pessoa.dataNascimento)} anos (${pessoa.dataNascimentoFormatada})`,
              pessoa?.cpf && `CPF ${pessoa.cpf}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <dl className={`${styles.campos} mb-0`}>
          <Campo rotulo='Protocolo'>{atendimento.protocolo}</Campo>
          <Campo rotulo='Situação'>
            {situacao && <StatusDot label={situacao.label} tone={situacao.tone} />}
          </Campo>
          <Campo rotulo='Solicitado em'>{atendimento.solicitacaoFormatada}</Campo>
          <Campo rotulo={t('empresa.label')}>{atendimento.empresa?.nomeFantasia}</Campo>
          <Campo rotulo='Solicitante'>{atendimento.solicitante?.nome}</Campo>
          <Campo rotulo='Lotação do solicitante'>
            {lotacao &&
              [
                lotacao.unidade?.nome,
                lotacao.cbo && `CBO ${lotacao.cbo.codigo}`,
                lotacao.ine && `INE ${lotacao.ine}`,
              ]
                .filter(Boolean)
                .join(' · ')}
          </Campo>
        </dl>

        {!lotacao && (
          <CorrigirLotacaoSolicitante atendimento={atendimento} onCorrigida={onAtualizar} />
        )}
      </div>
    </section>
  )
}
